import { ValidationRule } from "./types";

export const required = (path: string, message?: string): ValidationRule => ({
  path,
  validate: value => {
    if (Array.isArray(value)) {
      return (
        value.length > 0 &&
        value.every(
          item =>
            item !== null && item !== undefined && (typeof item !== "string" || item.trim() !== ""),
        )
      );
    }
    return (
      value !== null && value !== undefined && (typeof value !== "string" || value.trim() !== "")
    );
  },
  message: message || `${path.split(".").pop()} is required`,
});

export const minLength = (path: string, min: number, message?: string): ValidationRule => ({
  path,
  validate: value => {
    if (typeof value !== "string") {
      return false;
    }
    return value.length >= min;
  },
  message: message || `${path.split(".").pop()} must be at least ${min} characters`,
});

export const maxLength = (path: string, max: number, message?: string): ValidationRule => ({
  path,
  validate: value => {
    if (typeof value !== "string") {
      return true;
    }
    return value.length <= max;
  },
  message: message || `${path.split(".").pop()} must be no more than ${max} characters`,
});

export const arrayMinLength = (path: string, min: number, message?: string): ValidationRule => ({
  path,
  validate: value => {
    if (!Array.isArray(value)) {
      return false;
    }
    const validItems = value.filter(
      item =>
        item !== null && item !== undefined && (typeof item !== "string" || item.trim() !== ""),
    );
    return validItems.length >= min;
  },
  message: message || `Must have at least ${min} valid items`,
});

export const arrayMaxLength = (path: string, max: number, message?: string): ValidationRule => ({
  path,
  validate: value => {
    if (!Array.isArray(value)) {
      return true;
    }
    return value.length <= max;
  },
  message: message || `Must have no more than ${max} items`,
});

export const validUrl = (path: string, message?: string): ValidationRule => ({
  path,
  validate: value => {
    if (!value || typeof value !== "string") {
      return false;
    }
    try {
      const url = new URL(value);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  },
  message: message || "Must be a valid URL (http:// or https://)",
});

export const optionalUrl = (path: string, message?: string): ValidationRule => ({
  path,
  validate: value => {
    if (!value || (typeof value === "string" && value.trim() === "")) {
      return true;
    }
    if (typeof value !== "string") {
      return false;
    }
    try {
      const url = new URL(value);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  },
  message: message || "Must be a valid URL (http:// or https://) if provided",
});

export const validJson = (path: string, message?: string): ValidationRule => ({
  path,
  validate: value => {
    if (!value || typeof value !== "string") {
      return false;
    }
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  },
  message: message || "Must be valid JSON",
});

export const optionalJson = (path: string, message?: string): ValidationRule => ({
  path,
  validate: value => {
    if (!value || (typeof value === "string" && value.trim() === "")) {
      return true;
    }
    if (typeof value !== "string") {
      return false;
    }
    try {
      JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  },
  message: message || "Must be valid JSON if provided",
});

export const validRegex = (path: string, message?: string): ValidationRule => ({
  path,
  validate: value => {
    if (!value || typeof value !== "string") {
      return false;
    }
    try {
      new RegExp(value);
      return true;
    } catch {
      return false;
    }
  },
  message: message || "Must be a valid regular expression",
});

export const minValue = (path: string, min: number, message?: string): ValidationRule => ({
  path,
  validate: value => {
    const num = Number(value);
    if (isNaN(num)) {
      return false;
    }
    return num >= min;
  },
  message: message || `Must be at least ${min}`,
});

export const maxValue = (path: string, max: number, message?: string): ValidationRule => ({
  path,
  validate: value => {
    const num = Number(value);
    if (isNaN(num)) {
      return false;
    }
    return num <= max;
  },
  message: message || `Must be no more than ${max}`,
});

export const isNumber = (path: string, message?: string): ValidationRule => ({
  path,
  validate: value => {
    if (value === null || value === undefined || value === "") {
      return false;
    }
    return !isNaN(Number(value));
  },
  message: message || "Must be a valid number",
});

export const isInteger = (path: string, message?: string): ValidationRule => ({
  path,
  validate: value => {
    const num = Number(value);
    if (isNaN(num)) {
      return false;
    }
    return Number.isInteger(num);
  },
  message: message || "Must be a whole number",
});

export const oneOf = (
  path: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  allowedValues: any[],
  message?: string,
): ValidationRule => ({
  path,
  validate: value => allowedValues.includes(value),
  message: message || `Must be one of: ${allowedValues.join(", ")}`,
});

export const email = (path: string, message?: string): ValidationRule => ({
  path,
  validate: value => {
    if (!value || typeof value !== "string") {
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  },
  message: message || "Must be a valid email address",
});

export const conditional = <T>(
  path: string,
  condition: (payload: T) => boolean,
  rule: ValidationRule<T>,
): ValidationRule<T> => ({
  path,
  validate: (value, fullPayload) => {
    if (!condition(fullPayload)) {
      return true;
    }
    return rule.validate(value, fullPayload);
  },
  message: rule.message,
  severity: rule.severity,
  dependencies: [rule.path, ...(rule.dependencies || [])],
});

export const dependsOn = <T>(
  path: string,
  dependencyPath: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dependencyValue: any,
  rule: ValidationRule<T>,
): ValidationRule<T> => ({
  path,
  validate: (value, fullPayload) => {
    const dependencyPathValue = dependencyPath
      .split(".")
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .reduce((obj: any, key) => obj?.[key], fullPayload);
    if (dependencyPathValue !== dependencyValue) {
      return true;
    }
    return rule.validate(value, fullPayload);
  },
  message: rule.message,
  severity: rule.severity,
  dependencies: [dependencyPath, ...(rule.dependencies || [])],
});

export const custom = <T>(
  path: string,
  validator: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
    payload: T,
    allSources?: Record<string, string[]>,
  ) => boolean,
  message: string,
  dependencies?: string[],
): ValidationRule<T> => ({
  path,
  validate: validator,
  message,
  dependencies,
});

// Source-aware validation rules removed - sources infrastructure kept for future use

// Name uniqueness validation
export const uniqueBlockName = (message?: string): ValidationRule => ({
  path: "name",
  validate: (value, fullPayload, allSources) => {
    if (!value || typeof value !== "string" || !allSources) {
      return true;
    }

    const allExistingNames: string[] = [];
    Object.values(allSources).forEach(sourceArray => {
      allExistingNames.push(...sourceArray);
    });

    const nameOccurrences = allExistingNames.filter(name => name === value).length;

    return nameOccurrences === 0;
  },
  message: message || "Block name must be unique. This name is already in use.",
});

// Common validation rule groups
export const Rules = {
  name: (message?: string) => required("name", message || "Block name is required"),

  uniqueName: (message?: string) => [
    required("name", "Block name is required"),
    uniqueBlockName(message),
  ],

  source: (message?: string) => required("configuration.source", message || "Source is required"),

  sources: (min = 1, message?: string) => [
    required("configuration.sources", "Sources are required"),
    arrayMinLength("configuration.sources", min, message || `At least ${min} source(s) required`),
  ],

  url: (message?: string) => [
    required("configuration.url", "URL is required"),
    validUrl("configuration.url", message || "Must be a valid URL"),
  ],

  optionalUrl: (message?: string) => [
    optionalUrl("configuration.url", message || "Must be a valid URL if provided"),
  ],

  jsonValue: (field = "value", message?: string) => [
    required(`configuration.${field}`, `${field} is required`),
    validJson(`configuration.${field}`, message || "Must be valid JSON"),
  ],

  regexPattern: (field = "regex_pattern", message?: string) => [
    required(`configuration.${field}`, "Regex pattern is required"),
    validRegex(`configuration.${field}`, message || "Must be a valid regular expression"),
  ],

  numericValue: (field = "value", min?: number, max?: number) => {
    const rules = [
      required(`configuration.${field}`, `${field} is required`),
      isNumber(`configuration.${field}`, "Must be a valid number"),
    ];

    if (min !== undefined) {
      rules.push(minValue(`configuration.${field}`, min));
    }

    if (max !== undefined) {
      rules.push(maxValue(`configuration.${field}`, max));
    }

    return rules;
  },

  sliderConfig: () => [
    required("configuration.ui_element.min", "Minimum value is required"),
    required("configuration.ui_element.max", "Maximum value is required"),
    isNumber("configuration.ui_element.min", "Minimum must be a number"),
    isNumber("configuration.ui_element.max", "Maximum must be a number"),
    custom(
      "configuration.ui_element",
      value => {
        if (!value || typeof value !== "object") {
          return false;
        }
        const min = Number(value.min);
        const max = Number(value.max);
        return !isNaN(min) && !isNaN(max) && min < max;
      },
      "Minimum value must be less than maximum value",
      ["configuration.ui_element.min", "configuration.ui_element.max"],
    ),
  ],

  doubleSource: () => [
    required("configuration.sources.0", "First source is required"),
    required("configuration.sources.1", "Second source is required"),
  ],
} as const;
